import React, { FC, useState } from 'react';
import { Switch, DatePicker, message, Modal, Spin } from 'antd';
import { Button } from '@ui-modules';
import moment from 'moment';
import { useMutation } from 'react-query';
import FileSaver from 'file-saver';
import { ReactComponent as Calendar } from '@assets/svg/calendar.svg';
import { IListParams } from '@common/interfaces';
import { useRepository } from '@context';
import './styles.scss';

const { RangePicker } = DatePicker;

export interface IRangeDatePicker {
  from_date: string;
  to_date: string;
}

interface IAssignmentReportProps {
  facilityId: number;
  isModalVisible: boolean;
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const AssignmentReport: FC<IAssignmentReportProps> = ({
  facilityId,
  isModalVisible,
  setIsModalVisible,
}) => {
  const [isAwarded, setIsAwarded] = useState(true);
  const [filterDates, setFilterDates] = useState<IRangeDatePicker | null>(null);
  const [isRequesting, setIsRequesting] = useState<boolean>(false);

  const { freightRepository } = useRepository();

  const getExportFileMutation = useMutation(
    (params: IListParams) =>
      isAwarded
        ? freightRepository.getSelectedAssignmentsReportFile(facilityId, params)
        : freightRepository.getAllAssignmentsReportFile(facilityId, params),
    {
      onSuccess: (data: string) => {
        try {
          FileSaver.saveAs(
            new Blob([data], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
            }),
            `award_report_${filterDates?.from_date}-${filterDates?.to_date}.xlsx`,
          );
          setIsModalVisible(false);
          setFilterDates(null);
          setIsAwarded(true);
        } catch (error) {
          message.error('Parsing file error');
        }
      },
      onError: () => {
        message.error('Request failed, please try again.');
      },
      onSettled: () => {
        setIsRequesting(false);
      },
    },
  );

  return (
    <Modal
      footer={null}
      title={
        <div className="modal-title">
          <p>Export assignment report</p>
        </div>
      }
      width="500px"
      onCancel={() => {
        setIsModalVisible(false);
        setIsAwarded(true);
        setFilterDates(null);
      }}
      destroyOnClose
      visible={isModalVisible}
    >
      <div className="assignment-wrapper">
        <div className="award-toggle">
          <Switch
            checked={isAwarded}
            onChange={() => setIsAwarded(!isAwarded)}
            className="switch"
            checkedChildren="YES"
            unCheckedChildren="NO"
            disabled={isRequesting}
            defaultChecked
          />
          <p>
            Export <strong>only</strong> awarded quotations
          </p>
        </div>
        <p>Filter by award day range (date on which award was created)</p>
        <div>
          <span className="range-label">Range</span>
          <RangePicker
            format="DD-MM-YYYY"
            disabled={isRequesting}
            onChange={(value) => {
              if (value === null) return setFilterDates(null);
              return setFilterDates({
                from_date: value[0]?.format('YYYY-MM-DD') || '',
                to_date: value[1]?.format('YYYY-MM-DD') || '',
              });
            }}
            disabledDate={(current) => current && current > moment().endOf('day')}
            separator="to"
            suffixIcon={<Calendar className="calendar-icon" />}
          />
        </div>
        {isRequesting ? (
          <div className="loader-info">
            <Spin spinning />
            <p>Award assignment report is being generated...</p>
          </div>
        ) : (
          <Button
            onClick={() => {
              getExportFileMutation.mutate({
                ...filterDates,
              });
              setIsRequesting(true);
            }}
            className="export-button"
            variant="submit"
            disabled={!filterDates}
            text="Confirm"
          />
        )}
      </div>
    </Modal>
  );
};

export default AssignmentReport;
